import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { apiService, type Schema, type ConnectorConfig } from '../../services/api';
import { DRAFT_SCHEMAS_QUERY_KEY, listSchemas } from '../../services/schemaApi';
import { getConnectorCatalog } from '../../services/connectorApi';

const DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 3;

export function useSchemaQueries() {
  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commitSearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.length >= MIN_SEARCH_LENGTH) {
      setSearchTerm(trimmed);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = value.trim();
    if (trimmed === '') {
      commitSearch('');
      return;
    }

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      commitSearch(value);
    }, DEBOUNCE_MS);
  };

  const handleImmediateCommit = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    commitSearch(searchInput);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const listParams = useMemo(
    () => ({
      tenantId: tenantFilter.trim() || undefined,
      status: statusFilter.trim() || undefined,
      name: searchTerm || undefined,
    }),
    [tenantFilter, statusFilter, searchTerm]
  );

  const schemasQuery = useQuery(
    ['schemas', listParams],
    () => listSchemas(listParams),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const draftsQuery = useQuery<Schema[]>(
    DRAFT_SCHEMAS_QUERY_KEY,
    () => listSchemas({ status: 'draft', limit: 50 })
  );

  return {
    tenantFilter,
    setTenantFilter,
    statusFilter,
    setStatusFilter,
    searchInput,
    setSearchInput: handleSearchInputChange,
    searchTerm,
    commitSearch: handleImmediateCommit,
    listParams,
    schemasQuery,
    schemas: schemasQuery.data ?? [],
    /** @deprecated Use schemas instead */
    schemaSets: schemasQuery.data ?? [],
    draftsQuery,
    drafts: draftsQuery.data || [],
    isLoadingDrafts: draftsQuery.isLoading,
  };
}

/** @deprecated Use useSchemaQueries instead */
export const useSchemaSetQueries = useSchemaQueries;

export function useConnectorQueries(uploadDialogOpen: boolean, uploadMode: string) {
  const { data: savedConnectors } = useQuery<ConnectorConfig[]>(
    'all-connectors-for-schema',
    async () => {
      const [src, dest] = await Promise.all([
        apiService.listConnectors({ direction: 'source' }),
        apiService.listConnectors({ direction: 'destination' }),
      ]);
      return [...src, ...dest];
    },
    { enabled: uploadDialogOpen && uploadMode === 'connector' }
  );

  const { data: catalogForSchema } = useQuery<{ connectors: { connector_id: string; installed?: boolean }[] }>(
    'connector-catalog-for-schema',
    async () => {
      return getConnectorCatalog();
    },
    { enabled: uploadDialogOpen && uploadMode === 'connector', staleTime: 10 * 60 * 1000 }
  );

  const installedCatalogIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of catalogForSchema?.connectors || []) {
      if (c.installed !== false) ids.add(c.connector_id);
    }
    return ids;
  }, [catalogForSchema]);

  const usableConnectors = useMemo(() => {
    if (!savedConnectors) return [];
    return savedConnectors.filter((conn) => {
      const match = conn.connector_type.match(/^dlt_(.+)_(source|destination)$/);
      const catalogId = match?.[1];
      return catalogId ? installedCatalogIds.has(catalogId) : true;
    });
  }, [savedConnectors, installedCatalogIds]);

  return { savedConnectors, usableConnectors };
}
